import type { VariantProps } from 'class-variance-authority';
import { AnimatePresence, motion } from 'motion/react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TKey } from '~/lib/i18n-locales';
import { useAlertStore } from '~/modules/common/alerter/alert-store';
import { collapseMotion } from '~/modules/common/collapse-motion';
import type { IconComponent } from '~/modules/common/icons/types';
import { Alert, AlertDescription, AlertTitle, type alertVariants } from '~/modules/ui/alert';
import { useUIStore } from '~/modules/ui/ui-store';
import { cn } from '~/utils/cn';

export type AlertContextMode = 'public' | 'app';

interface AlertBannerProps {
  className?: string;
  id: string;
  modes?: AlertContextMode[];
  icon?: IconComponent;
  children: ReactNode;
  title?: TKey;
  variant?: VariantProps<typeof alertVariants>['variant'];
  animate?: boolean;
  contextMode?: AlertContextMode;
}

/** An entry of the app's `alertsConfig` (`alert-config.tsx`). */
export type AlertBanner = AlertBannerProps;

export function AlertBanner({
  id,
  icon: Icon,
  children,
  className = '',
  title,
  variant = 'default',
  animate = false,
  contextMode = 'app',
}: AlertBannerProps) {
  const { t } = useTranslation();
  const alertsSeen = useAlertStore((state) => state.alertsSeen);
  const setAlertSeen = useAlertStore((state) => state.setAlertSeen);
  const downAlert = useAlertStore((state) => state.downAlert);
  const publicAlertsSeen = useUIStore((state) => state.publicAlertsSeen);
  const setPublicAlertSeen = useUIStore((state) => state.setPublicAlertSeen);

  const isPublicContext = contextMode === 'public';
  const seenAlerts = isPublicContext ? publicAlertsSeen : alertsSeen;
  const setAsSeen = () => {
    if (isPublicContext) setPublicAlertSeen(id);
    else setAlertSeen(id);
  };
  const showAlert = !downAlert && !seenAlerts.includes(id);

  const alertContent = (
    <Alert variant={variant} onClose={setAsSeen} className={animate ? undefined : className}>
      {Icon && <Icon />}
      {title && <AlertTitle>{t(title)}</AlertTitle>}
      {children && <AlertDescription>{children}</AlertDescription>}
    </Alert>
  );

  if (animate) {
    return (
      <AnimatePresence initial={false}>
        {showAlert && (
          <motion.div key={id} {...collapseMotion} className={cn('overflow-hidden', className)}>
            {alertContent}
          </motion.div>
        )}
      </AnimatePresence>
    );
  }

  if (!showAlert) return null;
  return alertContent;
}
